/**
 * Compact work-mode selector in the top bar.
 *
 * The trigger takes the active mode's colour, on the border and the icon —
 * v2 did this and it is the fastest way to know which mode you are in without
 * reading anything.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWorkMode, WORK_MODES, type WorkMode } from "@/hooks/use-work-mode";
import { cn } from "@/lib/utils";

export function WorkModeSwitcher() {
  const { workMode, setWorkMode } = useWorkMode();
  const active = WORK_MODES.find((m) => m.value === workMode) ?? WORK_MODES[0];
  const ActiveIcon = active.icon;

  return (
    <Select
      value={workMode}
      onValueChange={(value) => setWorkMode(value as WorkMode)}
    >
      <SelectTrigger
        className={cn(
          "w-[130px] sm:w-[150px] h-9 border-2 transition-colors",
          active.border,
          active.text,
        )}
        aria-label={`Work mode: ${active.label}`}
      >
        <ActiveIcon className={cn("w-3.5 h-3.5 mr-1.5 shrink-0", active.text)} />
        {/* Explicit children so SelectValue shows the label only — by default
            it mirrors the matching SelectItem's content, which already has
            its own icon, and the two rendered side by side. */}
        <SelectValue>{active.label}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {WORK_MODES.map((mode) => {
          const Icon = mode.icon;
          return (
            <SelectItem key={mode.value} value={mode.value}>
              <span className="flex items-center gap-2">
                <Icon className={cn("w-3.5 h-3.5 shrink-0", mode.text)} />
                {mode.label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
